/**
 * FilterBar.jsx
 * 
 * Advanced filter controls for the GM Roster Builder.
 * Provides search, position filter, opponent rank filter, and edge filter.
 * 
 * Per Architect Guidance:
 * - Replaces simple search bar with comprehensive filtering
 * - Allows users to find "sleepers" via opponent rank filtering
 * - Uses filterState object instead of just searchTerm
 */

import React from 'react';
import { Search, ChevronDown, Shield, TrendingUp } from 'lucide-react';

// Position filter options
const POSITIONS = ['ALL', 'QB', 'RB', 'WR', 'TE'];

// Opponent rank filter options
const OPP_RANK_OPTIONS = [
  { value: 'ALL', label: 'All Matchups' },
  { value: 'SOFT', label: 'Soft (Bottom 10)' },
  { value: 'TOUGH', label: 'Tough (Top 10)' },
];

/**
 * FilterBar Component
 * 
 * @param {Object} filters - Current filter state
 * @param {string} filters.search - Search term
 * @param {string} filters.position - Position filter (ALL, QB, RB, WR, TE)
 * @param {string} filters.oppRank - Opponent rank filter (ALL, SOFT, TOUGH)
 * @param {boolean} filters.showPositiveEdge - Only show positive edge props
 * @param {Function} onChange - Callback when filters change
 * @param {string} slotPosition - Current slot position (for auto-filtering)
 */
const FilterBar = ({ filters, onChange, slotPosition = null }) => {
  const handleSearchChange = (e) => {
    onChange({ ...filters, search: e.target.value });
  };

  const handlePositionChange = (pos) => {
    onChange({ ...filters, position: pos });
  };

  const handleOppRankChange = (e) => {
    onChange({ ...filters, oppRank: e.target.value });
  };

  const handleEdgeToggle = () => {
    onChange({ ...filters, showPositiveEdge: !filters.showPositiveEdge });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-3 space-y-3">
      {/* Search Input */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search players..."
          value={filters.search}
          onChange={handleSearchChange}
          className="w-full pl-10 pr-4 py-2.5 bg-slate-100 rounded-lg text-sm 
                     placeholder:text-slate-400 focus:outline-none focus:ring-2 
                     focus:ring-indigo-500 focus:bg-white transition-all"
        />
      </div>

      {/* Position Filter Pills */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {POSITIONS.map((pos) => {
          // Determine if position should be enabled based on slot
          const isSlotRestricted = slotPosition && slotPosition !== 'FLEX';
          const slotPositions = slotPosition === 'FLEX' 
            ? ['RB', 'WR', 'TE'] 
            : [slotPosition?.replace(/[0-9]/g, '')];
          const isAllowed = !isSlotRestricted || pos === 'ALL' || slotPositions.includes(pos);
          
          return (
            <button
              key={pos}
              onClick={() => isAllowed && handlePositionChange(pos)}
              disabled={!isAllowed}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all
                ${filters.position === pos
                  ? 'bg-indigo-600 text-white'
                  : isAllowed
                    ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    : 'bg-slate-50 text-slate-300 cursor-not-allowed'
                }`}
            >
              {pos}
            </button>
          );
        })}
      </div>

      {/* Advanced Filters Row */}
      <div className="flex gap-2">
        {/* Opponent Rank Dropdown */}
        <div className="relative flex-1">
          <Shield size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <select
            value={filters.oppRank}
            onChange={handleOppRankChange}
            className="w-full pl-8 pr-8 py-2 bg-slate-100 rounded-lg text-xs font-medium
                       text-slate-700 appearance-none focus:outline-none focus:ring-2 
                       focus:ring-indigo-500 cursor-pointer"
          >
            {OPP_RANK_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>

        {/* Positive Edge Toggle */}
        <button
          onClick={handleEdgeToggle}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all
            ${filters.showPositiveEdge
              ? 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
        >
          <TrendingUp size={14} />
          <span>+Edge</span>
        </button>
      </div>
    </div>
  );
};

export default FilterBar;
